"use client";

import { useState, useEffect } from "react";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";

interface FootnoteTooltipProps {
  href: string;
  children: React.ReactNode;
}

const FootnoteTooltip = ({ href, children }: FootnoteTooltipProps) => {
  const [footnoteContent, setFootnoteContent] = useState<string>("");

  useEffect(() => {
    // DOM에서 각주 내용 찾기
    const findFootnoteContent = () => {
      // href에서 각주 번호 추출 (예: #user-content-fn-1 -> 1)
      const footnoteNumberMatch = href.match(/fn-(\d+)$/);
      if (!footnoteNumberMatch) {
        return "";
      }

      const footnoteNumber = parseInt(footnoteNumberMatch[1], 10);

      // footnotes 섹션 찾기
      const footnotesSection = document.querySelector(
        '[data-footnotes="true"], .footnotes'
      );
      if (!footnotesSection) {
        return "";
      }

      // footnotes 섹션 내의 li 요소들 찾기
      const footnoteItems = footnotesSection.querySelectorAll("ol > li");

      // 해당 번호의 각주 항목 찾기 (1-based index)
      const footnoteItem = footnoteItems[footnoteNumber - 1];
      if (!footnoteItem) {
        return "";
      }

      // 각주 내용에서 텍스트만 추출 (백링크 제외)
      const clone = footnoteItem.cloneNode(true) as Element;

      // 백링크 요소 제거 (↩, ↩︎ 등)
      const backlinks = clone.querySelectorAll(
        'a[href^="#fnref-"], a[href^="#user-content-fnref-"]'
      );
      backlinks.forEach((link) => link.remove());

      // HTML 내용 정리 (링크 구조 유지)
      let content = clone.innerHTML || "";
      content = content.replace(/↩︎?/g, "").trim(); // 백링크 기호 제거

      // 불필요한 클래스 정리 (p 태그의 색상 및 여백 클래스 제거)
      content = content.replace(/mb-6\s*/g, "");
      content = content.replace(/text-gray-500\s*/g, "");
      content = content.replace(/dark:text-white\s*/g, "");
      content = content.replace(
        /class="[^"]*leading-loose[^"]*"/g,
        'class="leading-loose"'
      );
      // 빈 클래스 속성 제거
      content = content.replace(/class=""\s*/g, "");
      content = content.replace(/class="\s*"\s*/g, "");

      // 링크 클래스 단순화 (visibility 개선)
      content = content.replace(
        /class="[^"]*inline-flex[^"]*"/g,
        'class="text-blue-600 dark:text-blue-400 hover:underline underline-offset-2 inline-flex items-center gap-1"'
      );

      return content;
    };

    // 컴포넌트 마운트 후 각주 내용 찾기 (더 긴 지연시간)
    const timer = setTimeout(() => {
      const content = findFootnoteContent();
      setFootnoteContent(content);
    }, 500);

    return () => clearTimeout(timer);
  }, [href]);

  if (!footnoteContent) {
    // 각주 내용을 찾지 못한 경우 기본 링크로 처리
    return (
      <a href={href} className="text-blue-600 hover:underline dark:text-blue-400">
        {children}
      </a>
    );
  }

  return (
    <Tooltip tapToOpen>
      <TooltipTrigger asChild>
        <button
          type="button"
          aria-label={`각주 ${children} 보기`}
          className="min-h-6 min-w-6 cursor-help rounded font-semibold text-foreground hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
        >
          [{children}]
        </button>
      </TooltipTrigger>
      <TooltipContent
        side="top"
        className="max-w-80 text-sm leading-relaxed md:!max-w-2xl !w-auto !text-left !text-wrap"
      >
        <div
          className="text-left [&_a]:!text-blue-600 [&_a]:!no-underline [&_a]:hover:!underline [&_a]:dark:!text-blue-400 [&_a_svg]:!size-3 [&_a_span]:!text-current [&_code]:!whitespace-normal [&_code]:!break-words [&_code]:!rounded [&_code]:!border [&_code]:!border-border [&_code]:!bg-muted [&_code]:!px-1 [&_code]:!py-0 [&_code]:!font-mono [&_code]:!text-[0.9em] [&_code]:!leading-relaxed [&_code]:!text-foreground"
          dangerouslySetInnerHTML={{ __html: footnoteContent }}
        />
      </TooltipContent>
    </Tooltip>
  );
};

export default FootnoteTooltip;
